import React from 'react';
import { Stack, Typography } from '@mui/material';
import NoteIcon from '@mui/icons-material/Note';
import { ShoppingCartProduct } from '@api/ShoppingCart/ShoppingCart';
import { getOrderLineDisplay } from '@utils/Order';
import { currencyFormatter } from '@utils/Numbers';

interface OrderLineDetailsProps {
    product: ShoppingCartProduct;
}

/**
 * Una línea del pedido en el detalle de `/orders`, como la lee cocina.
 * Renderer dual v1/v2 (`@utils/Order` `getOrderLineDisplay`): nunca desde el
 * formato crudo.
 */
const OrderLineDetails: React.FunctionComponent<OrderLineDetailsProps> = ({ product }) => {
    const line = getOrderLineDisplay(product);

    return (
        <Stack spacing={1} sx={{ mb: 3 }}>
            {/* Línea principal del producto */}
            <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="body1" fontWeight={600}>
                    {`${line.quantity}x`}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                    {line.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {currencyFormatter(line.total)}
                </Typography>
            </Stack>

            {/* Modificadores (variables/extras v1 · modifiers v2) */}
            {line.modifiers.map((modifier, idx) => (
                <Stack key={idx} direction="row" spacing={1} pl={2}>
                    <Typography variant="body2" color="text.secondary">
                        {`${modifier.quantity}x ${modifier.label}`}
                    </Typography>
                    {modifier.optionName && <Typography variant="body2">({modifier.optionName})</Typography>}
                    {modifier.extraCostTotal > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            +{currencyFormatter(modifier.extraCostTotal)}
                        </Typography>
                    )}
                </Stack>
            ))}

            {/* Ingredientes excluidos */}
            {line.removedIngredients.length > 0 && (
                <Stack spacing={0.5} pl={2}>
                    <Typography variant="body2" color="error.main" fontStyle="italic">
                        Sin:
                    </Typography>
                    {line.removedIngredients.map((ingredientName, idx) => (
                        <Typography key={idx} variant="body2" color="error.main" fontStyle="italic">
                            • {ingredientName}
                        </Typography>
                    ))}
                </Stack>
            )}

            {/* Nota del producto */}
            {line.note && (
                <Stack direction="row" spacing={1} bgcolor="#FFF8E1" p={1} borderRadius={1}>
                    <NoteIcon fontSize="small" color="disabled" />
                    <Typography variant="body2">{line.note}</Typography>
                </Stack>
            )}
        </Stack>
    );
};

export default OrderLineDetails;
